/* pamela-ai-scene-generator.js
   AI Scene Generator modal for custom laser-etching scenes: a guided step-by-step
   flow (subject / setting / extras) plus up to 4 reference photos, bundled into one
   "Generate" click, tied to the customer's design draft (not an account -- free tries
   are capped per draft). Results are watermarked previews; the full-resolution file
   only becomes available once the order actually completes. Requires
   pamela-monument-editor-v2.js to already be loaded (uses window.cmDraftToken/
   cmDesignId, window.PAMELA_placeLaserArtworkFromUrl, window.fitSelectedLaserArtworkToStone).
*/
(function () {
  "use strict";

  const API_BASE = "https://cm-editor-server.onrender.com";
  const MAX_REFERENCE_PHOTOS = 4;
  // Margin per side between the stone's edge and the usable engraving area -- a real
  // monument always has some foreground/border, and true edge-to-edge isn't offered
  // yet. "Fill the full stone" targets this inset area, not the raw stone dimensions.
  const STONE_MARGIN_IN = 1;

  const STEPS = [
    { key: "subject", question: "What's the main subject?", placeholder: "e.g. a 1978 Ford truck like my photo", photo: true },
    { key: "setting", question: "What's the setting/background?", placeholder: "e.g. a dirt path in the woods with mountains on the horizon", photo: true },
    { key: "extra1", question: "Anything else to include?", placeholder: "e.g. a whitetail buck and a bald eagle flying overhead", photo: true },
    { key: "extra2", question: "One more thing to add? (optional)", placeholder: "e.g. a stream with a bass jumping out of the water", photo: true }
  ];

  const PROGRESS_MESSAGES = [
    "Still working, please be patient...",
    "Making images is hard work...",
    "Mixing pixels and patience...",
    "Teaching the AI what your scene looks like...",
    "Good art takes a minute...",
    "Almost there, hang tight...",
    "Still on it -- this one's worth the wait..."
  ];

  function el(tag, attrs, cssText) {
    const n = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach((k) => {
        if (k === "text") n.textContent = attrs[k];
        else if (k === "html") n.innerHTML = attrs[k];
        else n.setAttribute(k, attrs[k]);
      });
    }
    if (cssText) n.style.cssText = cssText;
    return n;
  }

  function primaryBtnCss() {
    return "width:100%;padding:14px;border-radius:12px;border:1px solid #111;background:#111;color:#fff;" +
      "font:900 15px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;margin-top:6px;";
  }
  function secondaryBtnCss() {
    return "width:100%;padding:12px;border-radius:12px;border:1px solid #111;background:#fff;color:#111;" +
      "font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;margin-top:8px;";
  }

  // Product titles follow Width x Thickness x Height (confirmed against a real
  // example: "Companion Monument With 2 Vases -- 42x6x22 upright..." => 42in wide,
  // 6in thick, 22in tall) -- so the 1st and 3rd numbers are the real panel width and
  // height. Markers/slants are fixed at 14in/16in tall; uprights vary and can be much
  // taller, which is why "fill the full stone" and "custom height" are offered
  // separately below rather than assuming the full height is always wanted.
  function parsePanelDimsFromTitle() {
    try {
      const title = String(window.__PAMELA_PRODUCT_TITLE__ || "");
      const m = title.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
      if (!m) return { width: 0, height: 0 };
      const width = Number(m[1]);
      const height = Number(m[3]);
      return {
        width: Number.isFinite(width) && width > 0 ? width : 0,
        height: Number.isFinite(height) && height > 0 ? height : 0
      };
    } catch (_) {
      return { width: 0, height: 0 };
    }
  }

  async function uploadReferencePhoto(file) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "laser");
    fd.append("heic_ack", "true");
    const res = await fetch(`${API_BASE}/api/laser/upload`, { method: "POST", body: fd });
    const txt = await res.text().catch(() => "");
    let data = {};
    try { data = JSON.parse(txt); } catch (_) { data = {}; }
    if (!res.ok || !data.ok || !data.key) {
      throw new Error(data.error || "Photo upload failed.");
    }
    return data;
  }

  function friendlyErrorMessage(reason) {
    if (reason === "timeout") {
      return "Oops, this one ran out of time. Please contact Creative Monument for further assistance, or try again with fewer reference photos or a simpler description.";
    }
    return "Oops, something went wrong generating this one. Please try again, or contact Creative Monument for further assistance if it keeps happening.";
  }

  function freshSessionState() {
    const dims = parsePanelDimsFromTitle();
    // A monument's engraved area isn't the stone's full face -- there's always some
    // margin/foreground border, and true edge-to-edge isn't an option offered today.
    // So "fill the full stone" really means "fill the usable area inset from the
    // stone's edges", not the raw stone dimensions themselves.
    const insetWidthIn = dims.width > 0 ? Math.max(1, dims.width - 2 * STONE_MARGIN_IN) : 0;
    const insetHeightIn = dims.height > 0 ? Math.max(1, dims.height - 2 * STONE_MARGIN_IN) : 0;
    return {
      started: false,
      stepIndex: 0,
      answers: STEPS.map((s) => ({ question: s.question, text: "", key: s.key })),
      referenceKeys: [],
      referenceThumbs: [],
      generations: [],
      triesRemaining: null,
      busy: false,
      lastError: "",
      lastErrorReason: "",
      triesCheckFailed: false,
      parsedWidthIn: dims.width,
      parsedHeightIn: dims.height,
      insetWidthIn,
      insetHeightIn,
      sizeChoice: dims.width && dims.height ? "full" : "manual",
      panelWidthIn: insetWidthIn || dims.width,
      panelHeightIn: insetHeightIn || dims.height
    };
  }

  let state = freshSessionState();

  function ensurePanel() {
    let panel = document.getElementById("pamela-ai-scene-panel");
    if (panel) return panel;

    const backdrop = el(
      "div",
      { id: "pamela-ai-scene-backdrop" },
      "position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:2147483646;display:none;"
    );
    document.body.appendChild(backdrop);

    panel = el(
      "div",
      { id: "pamela-ai-scene-panel" },
      "position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:min(760px,94vw);" +
        "max-height:88vh;background:#fff;border-radius:16px;z-index:2147483647;" +
        "box-shadow:0 20px 60px rgba(0,0,0,.35);display:none;flex-direction:column;overflow:hidden;font-family:inherit;"
    );

    const header = el(
      "div", {},
      "padding:16px 18px;border-bottom:1px solid rgba(0,0,0,.12);display:flex;gap:10px;align-items:center;justify-content:space-between;flex:none;"
    );
    header.appendChild(el("div", { text: "AI Scene Generator" }, "font-weight:900;font-size:18px;color:#111;"));
    const closeBtn = el(
      "button",
      { type: "button", text: "x", "aria-label": "Close AI scene generator" },
      "width:40px;height:40px;border-radius:12px;border:1px solid rgba(0,0,0,.2);background:#fff;color:#111;font-size:22px;font-weight:900;cursor:pointer;"
    );
    closeBtn.addEventListener("click", close);
    header.appendChild(closeBtn);

    const body = el("div", { id: "pamela-ai-scene-body" }, "flex:1;overflow:auto;padding:18px;box-sizing:border-box;");

    panel.appendChild(header);
    panel.appendChild(body);
    document.body.appendChild(panel);
    return panel;
  }

  function render(body, opts) {
    if (state.triesRemaining === 0) {
      renderCappedOut(body, opts);
    } else if (state.stepIndex >= STEPS.length) {
      renderReview(body, opts);
    } else if (state.started) {
      renderStep(body, opts);
    } else {
      renderIntro(body, opts);
    }
  }

  // Mirrors laserPickSceneTierByStoneWidth() + the sceneCustom price calc in
  // pamela-monument-editor-v2.js's rebuildLaserSourceOptions() (not exposed on window,
  // so replicated here) -- this is a generated scene, priced identically to "upload
  // your own" custom scene art, not a separate product. Also applies the real
  // height-step surcharge (pamela-addon-config.js: sceneMaxHeightIn/sceneHeightStepIn/
  // heightStepPrice, ~20% of the tier's base per step per Pamela) -- each tier's base
  // price only covers scene height up to sceneMaxHeightIn (16in); every additional
  // sceneHeightStepIn (4in) beyond that adds one heightStepPrice unit.
  //
  // Returns a priceForHeight(heightIn) function plus the raw numbers, so both the
  // "full stone" and "custom height" options can show their OWN price at once instead
  // of one ambiguous shared number that only reflects whichever option happens to be
  // selected right now.
  function getSceneCustomPricing(widthIn) {
    const empty = { basePrice: 0, maxHeightIn: 0, stepIn: 0, stepPrice: 0, priceForHeight: () => 0 };
    try {
      const cfg = (window.PAMELA_ADDON_CONFIG && window.PAMELA_ADDON_CONFIG.laserEtching) || {};
      const tiers = Array.isArray(cfg.sceneStoneTiers) ? cfg.sceneStoneTiers : [];
      let tier = null;
      if (widthIn > 0 && tiers.length) {
        const sorted = tiers.slice().sort((a, b) => Number(a.maxStoneWidthIn) - Number(b.maxStoneWidthIn));
        tier = sorted.find((t) => widthIn <= Number(t.maxStoneWidthIn) + 1e-6) || null;
      }
      if (!tier) tier = tiers[0] || null;
      if (!tier) return empty;

      const basePrice = Number(tier.price || 0) + Number(cfg.sceneCustomPrepFee || 0);
      const maxHeightIn = Number(cfg.sceneMaxHeightIn || 0);
      const stepIn = Number(cfg.sceneHeightStepIn || 0);
      const stepPrice = Number(tier.heightStepPrice || 0);
      const priceForHeight = (heightIn) => {
        let total = basePrice;
        if (maxHeightIn > 0 && stepIn > 0 && Number(heightIn) > maxHeightIn) {
          total += Math.ceil((Number(heightIn) - maxHeightIn) / stepIn) * stepPrice;
        }
        return total > 0 ? total : 0;
      };
      return { basePrice, maxHeightIn, stepIn, stepPrice, priceForHeight };
    } catch (_) {
      return empty;
    }
  }

  function currentScenePrice() {
    const pricing = getSceneCustomPricing(state.parsedWidthIn || state.panelWidthIn || 0);
    return pricing.priceForHeight(state.panelHeightIn || 0);
  }

  function refreshSizeDependentUi() {
    const btn = document.getElementById("pamela-ai-scene-start-btn");
    if (btn) {
      const ok = state.panelWidthIn > 0 && state.panelHeightIn > 0;
      btn.disabled = !ok;
      btn.style.opacity = ok ? "1" : "0.4";
      btn.style.cursor = ok ? "pointer" : "not-allowed";
    }
    const customPriceNote = document.getElementById("pamela-ai-scene-custom-price-note");
    if (customPriceNote) {
      const pricing = getSceneCustomPricing(state.parsedWidthIn || 0);
      customPriceNote.textContent = state.panelHeightIn > 0
        ? `$${pricing.priceForHeight(state.panelHeightIn).toFixed(2)} for ${state.panelHeightIn}in tall.`
        : `Up to ${pricing.maxHeightIn}in: $${pricing.basePrice.toFixed(2)}. Each extra ${pricing.stepIn}in adds $${pricing.stepPrice.toFixed(2)}.`;
    }
    const summaryLine = document.getElementById("pamela-ai-scene-price-summary");
    if (summaryLine) {
      const price = currentScenePrice();
      summaryLine.textContent = price > 0
        ? `This selection: $${price.toFixed(2)} -- the same price as uploading your own custom scene image at this size.`
        : "";
    }
  }

  function renderIntro(body, opts) {
    body.innerHTML = "";
    body.appendChild(el(
      "div",
      { text: "Describe the scene you want, step by step. Feel free to attach a photo at each step to help us match it more closely -- like a photo of a vehicle, a landscape, or an animal. It's optional, so skip it if you'd rather not." },
      "font:800 13px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;margin-bottom:12px;"
    ));

    const sizeWrap = el("div", {}, "margin-bottom:14px;padding:12px;border-radius:10px;border:1px solid rgba(0,0,0,.12);");
    sizeWrap.appendChild(el("div", { text: "Image size" }, "font:900 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:8px;"));

    if (state.parsedWidthIn && state.parsedHeightIn) {
      // Pricing tier is keyed by the STONE's real width (not the inset image width) --
      // that's how the tiers are actually defined. Only the height surcharge cares
      // about the image's own (inset) height.
      const pricing = getSceneCustomPricing(state.parsedWidthIn);
      const fullFillPrice = pricing.priceForHeight(state.insetHeightIn);

      const makeSizeOption = (value, label) => {
        const row = el("label", {}, "display:flex;align-items:flex-start;gap:8px;margin-bottom:6px;font:800 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#333;cursor:pointer;");
        const radio = el("input", { type: "radio", name: "pamela-ai-scene-size" }, "margin-top:3px;");
        radio.checked = state.sizeChoice === value;
        radio.addEventListener("change", () => {
          state.sizeChoice = value;
          if (value === "full") {
            state.panelWidthIn = state.insetWidthIn;
            state.panelHeightIn = state.insetHeightIn;
          } else if (!state.panelHeightIn || state.panelHeightIn === state.insetHeightIn) {
            state.panelHeightIn = 0;
          }
          render(body, opts);
        });
        row.appendChild(radio);
        row.appendChild(el("span", { text: label }));
        return row;
      };

      sizeWrap.appendChild(makeSizeOption(
        "full",
        `Standard fill (${state.insetWidthIn}in x ${state.insetHeightIn}in image on your ${state.parsedWidthIn}in x ${state.parsedHeightIn}in stone) -- $${fullFillPrice.toFixed(2)}`
      ));
      sizeWrap.appendChild(makeSizeOption(
        "custom",
        `Custom height (up to ${state.insetWidthIn}in wide -- handy for leaving room for lettering) -- from $${pricing.basePrice.toFixed(2)} up to ${pricing.maxHeightIn}in`
      ));
      sizeWrap.appendChild(el(
        "div",
        { text: "True edge-to-edge images are available by contacting Creative Monument. We'll have that option added here soon." },
        "font:800 11px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#888;margin:2px 0 6px 26px;"
      ));

      if (state.sizeChoice === "custom") {
        const heightInput = el(
          "input",
          { type: "number", min: "1", step: "0.5", placeholder: "Height in inches" },
          "width:100%;margin-top:4px;padding:10px;border-radius:8px;border:1px solid #ddd;font:800 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-sizing:border-box;"
        );
        heightInput.value = state.panelHeightIn && state.panelHeightIn !== state.insetHeightIn ? state.panelHeightIn : "";
        heightInput.addEventListener("input", () => {
          const v = Number(heightInput.value);
          state.panelWidthIn = state.insetWidthIn;
          state.panelHeightIn = Number.isFinite(v) && v > 0 ? v : 0;
          refreshSizeDependentUi();
        });
        sizeWrap.appendChild(heightInput);

        const customPriceNote = el(
          "div",
          { id: "pamela-ai-scene-custom-price-note" },
          "font:800 11px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-top:4px;"
        );
        customPriceNote.textContent = state.panelHeightIn > 0
          ? `$${pricing.priceForHeight(state.panelHeightIn).toFixed(2)} for ${state.panelHeightIn}in tall.`
          : `Up to ${pricing.maxHeightIn}in: $${pricing.basePrice.toFixed(2)}. Each extra ${pricing.stepIn}in adds $${pricing.stepPrice.toFixed(2)}.`;
        sizeWrap.appendChild(customPriceNote);
      }
    } else {
      sizeWrap.appendChild(el(
        "div",
        { text: "We couldn't detect this product's size automatically -- please enter the width and height you want the image to fill." },
        "font:800 12px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-bottom:8px;"
      ));
      const dimsRow = el("div", {}, "display:flex;gap:8px;");
      const widthInput = el("input", { type: "number", min: "1", step: "0.5", placeholder: "Width (in)" }, "flex:1;padding:10px;border-radius:8px;border:1px solid #ddd;font:800 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-sizing:border-box;");
      const heightInput = el("input", { type: "number", min: "1", step: "0.5", placeholder: "Height (in)" }, "flex:1;padding:10px;border-radius:8px;border:1px solid #ddd;font:800 14px/1.2 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-sizing:border-box;");
      widthInput.value = state.panelWidthIn || "";
      heightInput.value = state.panelHeightIn || "";
      widthInput.addEventListener("input", () => {
        const v = Number(widthInput.value);
        state.panelWidthIn = Number.isFinite(v) && v > 0 ? v : 0;
        refreshSizeDependentUi();
      });
      heightInput.addEventListener("input", () => {
        const v = Number(heightInput.value);
        state.panelHeightIn = Number.isFinite(v) && v > 0 ? v : 0;
        refreshSizeDependentUi();
      });
      dimsRow.appendChild(widthInput);
      dimsRow.appendChild(heightInput);
      sizeWrap.appendChild(dimsRow);
    }
    body.appendChild(sizeWrap);

    const priceSummary = el(
      "div",
      { id: "pamela-ai-scene-price-summary" },
      "font:900 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:6px;padding:10px;border-radius:10px;background:#f5f5f5;"
    );
    const currentPrice = currentScenePrice();
    priceSummary.textContent = currentPrice > 0
      ? `This selection: $${currentPrice.toFixed(2)} -- the same price as uploading your own custom scene image at this size.`
      : "";
    body.appendChild(priceSummary);

    const triesLine = el("div", {}, "font:900 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:14px;padding:10px;border-radius:10px;background:#f5f5f5;");
    triesLine.textContent = typeof state.triesRemaining === "number"
      ? `${state.triesRemaining} free attempt${state.triesRemaining === 1 ? "" : "s"} left to find one you like, at no extra cost.`
      : "You'll see how many free generation attempts you have left before you generate.";
    body.appendChild(triesLine);

    if (state.triesCheckFailed) {
      body.appendChild(el(
        "div",
        { text: "We couldn't confirm how many free attempts are left on this design (a connection hiccup) -- if this design is already at its limit, generating may not work. Try closing and reopening this window, or continue and we'll let you know." },
        "font:800 11px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#8a6d00;margin:-4px 0 14px;padding:10px;border-radius:10px;background:#fff8e1;"
      ));
    }

    body.appendChild(el(
      "div",
      { text: "Results are AI-generated and won't be perfect every time -- clear, specific answers get better results. Previews are watermarked; you'll get the final full-resolution artwork once your order is placed." },
      "font:800 11px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-bottom:14px;"
    ));

    const sizeKnown = state.panelWidthIn > 0 && state.panelHeightIn > 0;
    const startBtn = el(
      "button",
      { type: "button", text: "Start", id: "pamela-ai-scene-start-btn" },
      primaryBtnCss() + (sizeKnown ? "" : "opacity:0.4;cursor:not-allowed;")
    );
    if (!sizeKnown) startBtn.setAttribute("disabled", "true");
    startBtn.addEventListener("click", () => {
      if (!(state.panelWidthIn > 0 && state.panelHeightIn > 0)) return;
      state.started = true;
      state.stepIndex = 0;
      render(body, opts);
    });
    body.appendChild(startBtn);
  }

  function renderStep(body, opts) {
    const step = STEPS[state.stepIndex];
    body.innerHTML = "";

    body.appendChild(el("div", { text: `Step ${state.stepIndex + 1} of ${STEPS.length}` }, "font:900 11px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#888;margin-bottom:6px;"));
    body.appendChild(el("div", { text: step.question }, "font:900 17px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:10px;"));

    const textarea = el(
      "textarea",
      { placeholder: step.placeholder },
      "width:100%;min-height:80px;padding:10px;border-radius:10px;border:1px solid #ddd;font:800 14px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;box-sizing:border-box;resize:vertical;"
    );
    textarea.value = state.answers[state.stepIndex].text || "";
    textarea.addEventListener("input", () => { state.answers[state.stepIndex].text = textarea.value; });
    body.appendChild(textarea);

    if (step.photo) {
      const photoRow = el("div", {}, "margin-top:10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap;");
      const fileInput = el("input", { type: "file", accept: "image/png,image/jpeg,image/heic,image/heif" }, "display:none;");
      const photoBtn = el("button", { type: "button", text: "Attach a photo" }, "flex:1 1 auto;padding:12px;border-radius:12px;border:1px solid #111;background:#fff;color:#111;font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;");
      const photoStatus = el("div", {}, "font:800 12px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#555;");

      const existing = state.referenceThumbs.find((t) => t.stepKey === step.key);
      if (existing) photoStatus.textContent = "Photo attached.";

      photoBtn.addEventListener("click", () => {
        if (state.referenceKeys.length >= MAX_REFERENCE_PHOTOS && !existing) {
          alert(`You can attach up to ${MAX_REFERENCE_PHOTOS} reference photos total.`);
          return;
        }
        fileInput.click();
      });
      fileInput.addEventListener("change", async () => {
        const file = fileInput.files && fileInput.files[0];
        if (!file) return;
        photoStatus.textContent = "Uploading...";
        try {
          const uploaded = await uploadReferencePhoto(file);
          if (existing) {
            state.referenceKeys = state.referenceKeys.filter((k) => k !== existing.key);
            state.referenceThumbs = state.referenceThumbs.filter((t) => t.stepKey !== step.key);
          }
          state.referenceKeys.push(uploaded.key);
          state.referenceThumbs.push({ stepKey: step.key, key: uploaded.key, url: uploaded.url });
          photoStatus.textContent = "Photo attached.";
        } catch (e) {
          photoStatus.textContent = "";
          alert(e && e.message ? e.message : "Photo upload failed.");
        }
      });

      photoRow.appendChild(photoBtn);
      photoRow.appendChild(fileInput);
      photoRow.appendChild(photoStatus);
      body.appendChild(photoRow);
    }

    const navRow = el("div", {}, "display:flex;gap:8px;margin-top:18px;");
    if (state.stepIndex > 0) {
      const backBtn = el("button", { type: "button", text: "Back" }, "flex:1;padding:12px;border-radius:12px;border:1px solid #111;background:#fff;color:#111;font:900 14px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;");
      backBtn.addEventListener("click", () => { state.stepIndex -= 1; render(body, opts); });
      navRow.appendChild(backBtn);
    }
    const isLast = state.stepIndex === STEPS.length - 1;
    const nextBtn = el("button", { type: "button", text: isLast ? "Review" : "Next" }, "flex:2;padding:14px;border-radius:12px;border:1px solid #111;background:#111;color:#fff;font:900 15px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;");
    nextBtn.addEventListener("click", () => {
      if (isLast) {
        const hasAnyText = state.answers.some((a) => a.text.trim());
        if (!hasAnyText) {
          alert("Tell us at least a little about what you want in the scene before continuing.");
          return;
        }
      }
      state.stepIndex += 1;
      render(body, opts);
    });
    navRow.appendChild(nextBtn);
    body.appendChild(navRow);
  }

  function renderReview(body, opts) {
    body.innerHTML = "";
    body.appendChild(el("div", { text: "Review your scene" }, "font:900 17px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:10px;"));

    state.answers.filter((a) => a.text.trim()).forEach((a) => {
      const row = el("div", {}, "margin-bottom:10px;padding:10px;border-radius:10px;background:#f5f5f5;");
      row.appendChild(el("div", { text: a.question }, "font:900 11px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-bottom:3px;"));
      row.appendChild(el("div", { text: a.text }, "font:800 14px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;"));
      body.appendChild(row);
    });

    if (state.referenceThumbs.length) {
      body.appendChild(el(
        "div",
        { text: `${state.referenceThumbs.length} reference photo${state.referenceThumbs.length === 1 ? "" : "s"} attached.` },
        "font:800 12px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#555;margin-bottom:6px;"
      ));
    }

    body.appendChild(el(
      "div",
      { text: `Target size: ${state.panelWidthIn}in x ${state.panelHeightIn}in` },
      "font:800 12px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#555;margin-bottom:14px;"
    ));

    if (typeof state.triesRemaining === "number") {
      body.appendChild(el(
        "div",
        { text: `${state.triesRemaining} free attempt${state.triesRemaining === 1 ? "" : "s"} left.` },
        "font:900 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:12px;"
      ));
    }

    const backBtn = el("button", { type: "button", text: "Back" }, secondaryBtnCss());
    backBtn.addEventListener("click", () => { state.stepIndex = STEPS.length - 1; render(body, opts); });
    body.appendChild(backBtn);

    // No cap-reached branch here -- render() routes to renderCappedOut() before ever
    // reaching this function once state.triesRemaining hits 0, so this only ever runs
    // while the customer still has tries available.
    const genBtn = el(
      "button",
      { type: "button", text: state.busy ? "Generating..." : "Generate" },
      primaryBtnCss() + (state.busy ? "opacity:0.4;cursor:not-allowed;" : "")
    );
    if (state.busy) genBtn.setAttribute("disabled", "true");
    genBtn.addEventListener("click", () => { if (!state.busy) runGeneration(body, opts); });
    body.appendChild(genBtn);

    if (state.busy) {
      body.appendChild(el(
        "div",
        { id: "pamela-ai-scene-progress-msg" },
        "font:800 12px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-top:10px;text-align:center;min-height:16px;"
      ));
    }

    if (state.lastError) {
      body.appendChild(el(
        "div",
        { text: state.lastError },
        "font:800 12px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#b00020;margin-top:10px;"
      ));
      if (state.lastErrorReason) {
        body.appendChild(el(
          "div",
          { text: `(ref: ${state.lastErrorReason})` },
          "font:700 10px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#aaa;margin-top:3px;"
        ));
      }
    }

    renderResults(body, opts);
  }

  // Shown instead of the intro/guided-flow whenever the customer has no free tries
  // left on this design -- no point walking them through 4 questions just to hit a
  // disabled button at the end. Still shows everything they've already generated
  // (restored in open() from the server, so this works even on a fresh reopen).
  function renderCappedOut(body, opts) {
    body.innerHTML = "";
    body.appendChild(el(
      "div",
      { text: "AI Scene Generator" },
      "font:900 17px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin-bottom:10px;"
    ));
    body.appendChild(el(
      "div",
      {
        html: "We're sorry, you have reached your free try limit. Since new generations cost Creative Monument, " +
          "we ask that you choose the one closest to your vision for now. Save that, then contact us through text at " +
          "<a href=\"sms:+15015810300\" style=\"color:#b00020;font-weight:900;\">501-581-0300</a>. Once you have " +
          "placed an order, we will perfect the scene further with you."
      },
      "font:800 13px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#b00020;margin-bottom:10px;padding:10px;border-radius:10px;background:#fdf1f1;"
    ));
    renderResults(body, opts);
    if (!state.generations.length) {
      body.appendChild(el(
        "div",
        { text: "No saved results were found on this design -- text the number above and we'll help from here." },
        "font:800 12px/1.4 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#666;margin-top:10px;"
      ));
    }
  }

  function renderResults(body, opts) {
    if (!state.generations.length) return;
    body.appendChild(el("div", { text: "Your results" }, "font:900 15px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#111;margin:18px 0 10px;"));
    state.generations.forEach((g) => {
      const card = el("div", {}, "margin-bottom:14px;padding:10px;border-radius:12px;border:1px solid rgba(0,0,0,.12);");
      const img = el("img", { src: g.preview_url, alt: "Generated scene preview" }, "width:100%;border-radius:8px;display:block;margin-bottom:8px;");
      card.appendChild(img);
      const btnRow = el("div", {}, "display:flex;gap:8px;");

      const downloadBtn = el(
        "button",
        { type: "button", text: "Download" },
        "flex:1;padding:12px;border-radius:12px;border:1px solid #111;background:#fff;color:#111;font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;"
      );
      downloadBtn.addEventListener("click", async () => {
        // Forces an actual download without ever navigating this page -- an <a download>
        // link doesn't work cross-origin (the browser just opens/navigates instead,
        // which is what was losing the whole guided-flow session).
        const original = downloadBtn.textContent;
        downloadBtn.textContent = "Downloading...";
        try {
          const resp = await fetch(g.preview_url);
          if (!resp.ok) throw new Error("Download failed.");
          const blob = await resp.blob();
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `creative-monument-ai-scene-preview-${g.generation_id}.jpg`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
        } catch (e) {
          window.open(g.preview_url, "_blank", "noopener,noreferrer");
        } finally {
          downloadBtn.textContent = original;
        }
      });

      const applyBtn = el("button", { type: "button", text: "Apply to Monument" }, "flex:1;padding:12px;border-radius:12px;border:1px solid #111;background:#111;color:#fff;font:900 13px/1 system-ui,-apple-system,Segoe UI,Roboto,Arial;cursor:pointer;");
      applyBtn.addEventListener("click", () => applyGenerationToMonument(g, opts));
      btnRow.appendChild(downloadBtn);
      btnRow.appendChild(applyBtn);
      card.appendChild(btnRow);
      body.appendChild(card);
    });
  }

  function startProgressMessages(statusEl) {
    let idx = 0;
    let timer = setTimeout(function tick() {
      if (statusEl) statusEl.textContent = PROGRESS_MESSAGES[idx % PROGRESS_MESSAGES.length];
      idx++;
      timer = setTimeout(tick, 13000);
    }, 15000);
    return function stop() { clearTimeout(timer); };
  }

  async function runGeneration(body, opts) {
    if (state.busy) return;
    state.busy = true;
    state.lastError = "";
    state.lastErrorReason = "";
    render(body, opts);
    const stopProgress = startProgressMessages(document.getElementById("pamela-ai-scene-progress-msg"));
    try {
      const token = String(window.cmDraftToken || "").trim();
      const designId = String(window.cmDesignId || "").trim();
      if (!token || !designId) {
        state.lastError = "Please save your design before generating a scene.";
        return;
      }

      const res = await fetch(`${API_BASE}/api/ai-scene/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          design_id: designId,
          side: (opts && opts.side) || (window.PAMELA_SIDES && window.PAMELA_SIDES.current) || "front",
          answers: state.answers.filter((a) => a.text.trim()),
          reference_keys: state.referenceKeys,
          panel_width_in: state.panelWidthIn || 0,
          panel_height_in: state.panelHeightIn || 0
        })
      });
      const txt = await res.text().catch(() => "");
      let data = {};
      try { data = JSON.parse(txt); } catch (_) { data = {}; }

      if (res.status === 403 && data.code === "CAP_REACHED") {
        state.triesRemaining = 0;
        return;
      }
      if (!res.ok || !data.ok) {
        state.lastError = friendlyErrorMessage(data.reason);
        state.lastErrorReason = data.reason || data.error || `http_${res.status}`;
        return;
      }

      state.triesRemaining = data.tries_remaining;
      state.generations.unshift({ generation_id: data.generation_id, preview_url: data.preview_url });
    } catch (e) {
      state.lastError = friendlyErrorMessage(null);
      state.lastErrorReason = "client_" + (e && e.name ? e.name : "exception");
    } finally {
      stopProgress();
      state.busy = false;
      render(body, opts);
    }
  }

  async function applyGenerationToMonument(generation, opts) {
    try {
      if (!window.PAMELA_placeLaserArtworkFromUrl) throw new Error("The editor is not ready yet.");
      await window.PAMELA_placeLaserArtworkFromUrl(generation.preview_url, {
        source: "custom",
        laser_kind: "scene",
        side: (opts && opts.side) || (window.PAMELA_SIDES && window.PAMELA_SIDES.current) || "front",
        ai_scene_generation_id: generation.generation_id,
        name: "AI Generated Scene"
      });
      close();
      try { window.fitSelectedLaserArtworkToStone && window.fitSelectedLaserArtworkToStone(); } catch (_) {}
    } catch (e) {
      alert(e && e.message ? e.message : "Could not apply this image to your monument.");
    }
  }

  // A silent failure here is exactly how a customer could get gated incorrectly (or not
  // gated when they should be) without any trace to diagnose it by -- confirmed live: a
  // customer went through all 4 guided-flow steps on a design that was actually already
  // at 0 tries, only getting blocked at the very end by the backend's own (correct)
  // check. This fetch is the ONLY thing standing between that and catching it upfront,
  // so it gets a real timeout, one retry, and actual error logging instead of a bare
  // catch(_){} that swallows the problem with no trace.
  async function fetchDraftInfo(token, attempt) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(`${API_BASE}/api/draft/${encodeURIComponent(token)}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`draft fetch returned ${res.status}`);
      const data = await res.json();
      if (!data || !data.draft || typeof data.draft.ai_scene_tries_remaining !== "number") {
        throw new Error("draft fetch response missing ai_scene_tries_remaining");
      }
      return data;
    } catch (e) {
      console.error(`[ai-scene] draft info fetch failed (attempt ${attempt}):`, e);
      if (attempt < 2) return fetchDraftInfo(token, attempt + 1);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  async function open(options) {
    const opts = options || {};
    const panel = ensurePanel();
    state = freshSessionState();
    panel.style.display = "flex";
    const backdrop = document.getElementById("pamela-ai-scene-backdrop");
    if (backdrop) backdrop.style.display = "block";
    const body = document.getElementById("pamela-ai-scene-body");

    const token = String(window.cmDraftToken || "").trim();
    if (token) {
      const data = await fetchDraftInfo(token, 1);
      if (data) {
        state.triesRemaining = data.draft.ai_scene_tries_remaining;
        // Past generations already exist server-side (the server returns them on every
        // draft fetch) -- restore them here so a customer who closes and reopens the
        // modal, or hits the cap, still sees and can use/download what they already
        // generated instead of it silently vanishing.
        const pastGenerations = Array.isArray(data?.draft?.ai_scene_generations_json)
          ? data.draft.ai_scene_generations_json
          : [];
        state.generations = pastGenerations
          .filter((g) => g && g.preview_key)
          .map((g) => ({
            generation_id: g.id,
            preview_url: `${API_BASE}/api/laser/file/${encodeURIComponent(g.preview_key)}`
          }))
          .reverse();
      } else {
        // Both attempts failed -- do not silently proceed as if everything's fine (that's
        // exactly the bug this replaces). Leave triesRemaining as null (not 0, since we
        // genuinely don't know) and flag it so the intro screen can say so honestly.
        state.triesCheckFailed = true;
      }
    }

    render(body, opts);
  }

  function close() {
    const panel = document.getElementById("pamela-ai-scene-panel");
    if (panel) panel.style.display = "none";
    const backdrop = document.getElementById("pamela-ai-scene-backdrop");
    if (backdrop) backdrop.style.display = "none";
  }

  window.PAMELA_AI_SCENE_UI = { open, close };
})();
